import { useMemo } from "react";
import { Trophy, Percent } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const pct = (n) => `${n || 0}%`;

export const DealerScorecard = ({ board, onPickDealer }) => {
  const rows = board?.rows || [];
  const totals = board?.totals || {};

  const stats = useMemo(
    () => [
      ["Dealers", totals.dealers ?? 0],
      ["Enquiries", totals.enquiries ?? 0],
      ["Site visits", totals.site_visits ?? 0],
      ["Closed", totals.closed ?? 0],
    ],
    [totals]
  );

  return (
    <div data-testid="dealer-scorecard">
      <p className="mb-4 flex items-center gap-2 text-[13px] text-muted-foreground">
        <Trophy className="h-4 w-4 text-primary" /> Ranked by enquiry volume, then closures. Click a dealer to see their
        enquiries.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(([label, value]) => (
          <div
            key={label}
            className="rounded-2xl border border-border p-5"
            data-testid={`scorecard-stat-${label.toLowerCase().replace(/ /g, "-")}`}
          >
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-2xl">{value}</p>
          </div>
        ))}
      </div>

      <div className="mt-5 rounded-2xl border border-border overflow-x-auto">
        <Table className="min-w-[940px]" data-testid="dealer-scorecard-table">
          <TableHeader>
            <TableRow>
              <TableHead>#</TableHead>
              <TableHead>Dealer</TableHead>
              <TableHead>Listings</TableHead>
              <TableHead>Enquiries</TableHead>
              <TableHead>Site visits</TableHead>
              <TableHead>Closed</TableHead>
              <TableHead>Open</TableHead>
              <TableHead>Visit rate</TableHead>
              <TableHead>Close rate</TableHead>
              <TableHead>Last enquiry</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} className="text-sm text-muted-foreground" data-testid="scorecard-empty">
                  No dealer activity yet.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r, i) => (
                <TableRow key={r.dealer_name} data-testid={`scorecard-row-${r.dealer_name}`}>
                  <TableCell className="text-[13px] font-bold text-muted-foreground">{i + 1}</TableCell>
                  <TableCell>
                    <button
                      onClick={() => onPickDealer?.(r.dealer_name)}
                      className="text-left text-sm font-bold underline-offset-4 transition-colors duration-200 hover:text-primary hover:underline"
                      data-testid={`scorecard-pick-${r.dealer_name}`}
                    >
                      {r.dealer_name}
                    </button>
                    {r.dealer_phone && (
                      <span className="block text-[12px] text-muted-foreground">{r.dealer_phone}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">
                    {r.listings}
                    <span className="block text-[11px] text-muted-foreground">{r.active_listings} active</span>
                  </TableCell>
                  <TableCell className="text-sm font-bold">{r.enquiries}</TableCell>
                  <TableCell className="text-sm">{r.site_visits}</TableCell>
                  <TableCell className="text-sm">{r.closed}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{r.new + r.in_progress}</TableCell>
                  <TableCell>
                    <RateBar value={r.visit_rate} />
                  </TableCell>
                  <TableCell>
                    <RateBar value={r.close_rate} accent />
                  </TableCell>
                  <TableCell className="text-xs whitespace-nowrap">
                    {(r.last_enquiry_at || "").slice(0, 10) || "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

const RateBar = ({ value, accent }) => (
  <div className="min-w-[92px]">
    <p className="flex items-center gap-0.5 text-[13px] font-bold">
      {value || 0}
      <Percent className="h-3 w-3" />
    </p>
    <div className="mt-1 h-1.5 w-full rounded-full bg-muted">
      <div
        className={`h-full rounded-full transition-[width] duration-500 ${accent ? "bg-primary" : "bg-foreground/60"}`}
        style={{ width: `${Math.min(value || 0, 100)}%` }}
      />
    </div>
  </div>
);

export default DealerScorecard;
